import type {RefObject} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {Component, Editor} from 'grapesjs';
import type {EmailImageCropModeState} from '@EmailMakerReact/lib/email-maker-image-crop-library';
import {subscribeToZoomChange} from '@EmailMakerReact/lib/email-maker-zoom-library';
import {getImageCropOverlayGeometry, isSameImageCropOverlayGeometry} from './image-crop-overlay.library';
import type {ImageCropOverlayGeometry, MeasuredImageCropOverlayGeometry} from './image-crop-overlay.types';

interface ImageBoxTrackingOptions {
  editor: Editor;
  component: Component;
  layerRef: RefObject<HTMLDivElement | null>;
  scheduleMeasureRef: RefObject<(() => void) | null>;
  setMeasured: (update: (previous: MeasuredImageCropOverlayGeometry | null) => MeasuredImageCropOverlayGeometry | null) => void;
}

export function useImageCropOverlayGeometry(
  editor: Editor,
  layerRef: RefObject<HTMLDivElement | null>,
  cropModeState: EmailImageCropModeState | null
): ImageCropOverlayGeometry | null {
  const [measured, setMeasured] = useState<MeasuredImageCropOverlayGeometry | null>(null);
  const scheduleMeasureRef = useRef<(() => void) | null>(null);
  const component = cropModeState?.component ?? null;

  useEffect(() => {
    if (!component) {
      return undefined;
    }
    return trackImageBox({editor, component, layerRef, scheduleMeasureRef, setMeasured});
  }, [editor, component, layerRef]);

  useEffect(() => {
    scheduleMeasureRef.current?.();
  }, [cropModeState]);

  if (!component || measured?.component !== component) {
    return null;
  }
  return measured.geometry;
}

function trackImageBox({editor, component, layerRef, scheduleMeasureRef, setMeasured}: ImageBoxTrackingOptions): () => void {
  const image = component.getEl() ?? null;
  const frameElement = editor.Canvas.getFrameEl();
  let animationFrameId = 0;

  const measure = (): void => {
    const layer = layerRef.current;
    if (!layer || !image) {
      return;
    }
    const geometry = measureImageCropOverlayGeometry(layer, frameElement, image);
    setMeasured((previous) => {
      if (previous?.component === component && isSameImageCropOverlayGeometry(previous.geometry, geometry)) {
        return previous;
      }
      return {component, geometry};
    });
  };
  const scheduleMeasure = (): void => {
    if (animationFrameId !== 0) {
      return;
    }
    animationFrameId = window.requestAnimationFrame((): void => {
      animationFrameId = 0;
      measure();
    });
  };

  const stopTrackers = [
    trackResizes([frameElement, image, layerRef.current], scheduleMeasure),
    trackImageLoad(image, scheduleMeasure),
    trackScrolls(editor, scheduleMeasure),
    trackZoomTransition(editor.Canvas.getBody(), measure),
    subscribeToZoomChange(editor, scheduleMeasure),
  ];
  scheduleMeasureRef.current = scheduleMeasure;
  measure();

  return (): void => {
    window.cancelAnimationFrame(animationFrameId);
    scheduleMeasureRef.current = null;
    stopTrackers.forEach((stopTracker) => {
      stopTracker();
    });
  };
}

function measureImageCropOverlayGeometry(layer: HTMLElement, frameElement: HTMLIFrameElement, image: HTMLElement): ImageCropOverlayGeometry {
  return getImageCropOverlayGeometry({
    layerRect: layer.getBoundingClientRect(),
    frameRect: frameElement.getBoundingClientRect(),
    frameClientLeft: frameElement.clientLeft,
    frameClientTop: frameElement.clientTop,
    frameClientWidth: frameElement.clientWidth,
    frameClientHeight: frameElement.clientHeight,
    imageRect: image.getBoundingClientRect(),
  });
}

function trackResizes(elements: (HTMLElement | null)[], onResize: () => void): () => void {
  const resizeObserver = new ResizeObserver(onResize);
  elements.forEach((element) => {
    if (element) {
      resizeObserver.observe(element);
    }
  });
  window.addEventListener('resize', onResize);
  return (): void => {
    resizeObserver.disconnect();
    window.removeEventListener('resize', onResize);
  };
}

function trackImageLoad(image: HTMLElement | null, onLoad: () => void): () => void {
  image?.addEventListener('load', onLoad);
  return (): void => {
    image?.removeEventListener('load', onLoad);
  };
}

function trackScrolls(editor: Editor, onScroll: () => void): () => void {
  const frameWindow = editor.Canvas.getWindow();
  const frameDocument = editor.Canvas.getDocument();
  frameWindow.addEventListener('scroll', onScroll, {passive: true});
  frameDocument.addEventListener('scroll', onScroll, {capture: true, passive: true});
  return (): void => {
    frameWindow.removeEventListener('scroll', onScroll);
    frameDocument.removeEventListener('scroll', onScroll, {capture: true});
  };
}

function trackZoomTransition(frameBody: HTMLElement, onFrame: () => void): () => void {
  let animationFrameId = 0;

  const runFrame = (): void => {
    onFrame();
    animationFrameId = window.requestAnimationFrame(runFrame);
  };
  const stopFrames = (): void => {
    window.cancelAnimationFrame(animationFrameId);
    animationFrameId = 0;
  };
  const onTransitionStart = (event: TransitionEvent): void => {
    if (event.target === frameBody && animationFrameId === 0) {
      animationFrameId = window.requestAnimationFrame(runFrame);
    }
  };
  const onTransitionEnd = (event: TransitionEvent): void => {
    if (event.target === frameBody) {
      stopFrames();
      onFrame();
    }
  };

  frameBody.addEventListener('transitionrun', onTransitionStart);
  frameBody.addEventListener('transitionend', onTransitionEnd);
  frameBody.addEventListener('transitioncancel', onTransitionEnd);
  return (): void => {
    stopFrames();
    frameBody.removeEventListener('transitionrun', onTransitionStart);
    frameBody.removeEventListener('transitionend', onTransitionEnd);
    frameBody.removeEventListener('transitioncancel', onTransitionEnd);
  };
}
